import { useEffect, useId, useLayoutEffect, useRef, useState } from 'react';
import { KeyCaps } from '@shared/components/KeyCaps';
import { View } from '@shared/components/View';
import { useShowHotkeyTooltips } from '@shared/util/hotkeys';
import { supportsAnchorPositioning } from '@shared/util/supports';
import { TooltipTrigger, observeTooltips } from './observeTooltips';
import styles from './TooltipPresenter.module.css';

const activeAnchorName = '--active-tooltip';

/**
 * The single tooltip for every `data-tooltip` trigger (see `getTooltipProps`).
 * Mount once per app root.
 */
export function TooltipPresenter() {
  const [trigger, setTrigger] = useState<TooltipTrigger | null>(null);
  const tooltipRef = useRef<HTMLDivElement>(null);
  const textRef = useRef<HTMLSpanElement>(null);
  const tooltipId = useId();
  const [showHotkeyTooltips] = useShowHotkeyTooltips();

  const hotkey = showHotkeyTooltips ? trigger?.hotkey : null;
  const hasContent = !!trigger?.text || !!hotkey;
  const element = hasContent ? trigger?.element : undefined;

  useEffect(() => {
    if (!supportsAnchorPositioning) {
      return;
    }
    return observeTooltips({
      onShow: setTrigger,
      onHide: () => {
        hidePopover(tooltipRef.current);
        setTrigger(null);
      },
    });
  }, []);

  useLayoutEffect(() => {
    const tooltipEl = tooltipRef.current;
    if (!element || !tooltipEl) {
      return;
    }

    const anchor = anchorTo(element);
    const restoreDescribedBy = describeBy(element, tooltipId);
    tooltipEl.style.positionAnchor = anchor.name;
    tooltipEl.showPopover();
    // Moving straight to another trigger hides and reshows the popover in one
    // frame, which never restarts its CSS entry animation. Restart it here so
    // every trigger gets one.
    tooltipEl.getAnimations().forEach(animation => {
      animation.cancel();
      animation.play();
    });

    return () => {
      hidePopover(tooltipEl);
      restoreDescribedBy();
      anchor.restore();
    };
  }, [element, tooltipId]);

  const text = trigger?.text;
  useLayoutEffect(() => {
    if (element && tooltipRef.current) {
      shrinkWrap(tooltipRef.current, textRef.current);
    }
  }, [element, text, hotkey]);

  return (
    <div
      ref={tooltipRef}
      id={tooltipId}
      role="tooltip"
      className={styles.tooltip}
      popover="manual"
      data-theme="default"
      data-position={trigger?.position}
    >
      {text && (
        <span ref={textRef} className={styles.text}>
          {text}
        </span>
      )}
      {hotkey && (
        <View center className={styles.keycaps}>
          <KeyCaps hotkey={hotkey} />
        </View>
      )}
    </div>
  );
}

/**
 * Wrapped text leaves the box at its `max-width`, however short the lines end up
 * (`text-wrap: balance` makes them shorter still). Narrows the box to the widest
 * line. Runs after the popover opens, since a closed one has no layout.
 */
function shrinkWrap(tooltipEl: HTMLElement, textEl: HTMLElement | null) {
  tooltipEl.style.width = '';
  const textNode = textEl?.firstChild;
  if (!textEl || !textNode) {
    return;
  }

  // One rect per line. The span itself is a flex item, so its own rects don't
  // split by line. Lines past the line clamp are still laid out, just hidden,
  // so only the ones inside the span count.
  const range = document.createRange();
  range.selectNodeContents(textNode);
  const textRect = textEl.getBoundingClientRect();
  const lines = Array.from(range.getClientRects()).filter(line => line.top < textRect.bottom);
  if (lines.length < 2) {
    return;
  }

  const widestLine = Math.max(...lines.map(line => line.width));
  const unused = textRect.width - widestLine;
  // Rounded up so subpixel widths can't push the last word onto a new line.
  tooltipEl.style.width = `${Math.ceil(tooltipEl.getBoundingClientRect().width - unused)}px`;
}

function hidePopover(tooltipEl: HTMLElement | null) {
  if (tooltipEl?.matches(':popover-open')) {
    tooltipEl.hidePopover();
  }
}

/**
 * Reuses the trigger's own anchor name when it has one, so elements that are
 * already anchors (menus, pickers) are left untouched.
 */
function anchorTo(element: HTMLElement) {
  const existing = getComputedStyle(element).anchorName;
  const reusable = existing
    .split(',')
    .map(name => name.trim())
    .find(name => name.startsWith('--'));
  if (reusable) {
    return { name: reusable, restore: () => {} };
  }

  const inline = element.style.anchorName;
  element.style.anchorName = activeAnchorName;
  return {
    name: activeAnchorName,
    restore: () => {
      element.style.anchorName = inline;
    },
  };
}

function describeBy(element: HTMLElement, tooltipId: string) {
  const previous = element.getAttribute('aria-describedby');
  element.setAttribute('aria-describedby', previous ? `${previous} ${tooltipId}` : tooltipId);
  return () => {
    if (previous === null) {
      element.removeAttribute('aria-describedby');
    } else {
      element.setAttribute('aria-describedby', previous);
    }
  };
}
